// docs/41 PZ8: what of a puzzle room's workings the board may draw. Without hints the board shows
// only what anyone standing in the room could see: plates pressed, levers thrown, runes lit, gates
// shut. Which driver works which gate (the wires), how many drivers a gate wants (its pips) and how
// far the lock's sequence has got (its pips) are the puzzle's answer, so they are drawn only around
// a fixture that a bought Nudge or Step pointed at. A Nudge shows that fixture's own wires; a Step,
// which spells the move out, shows every driver of the gate it is about.
import type { FixtureView } from '../sim/types';

export interface HintReveal {
  /** Wires to draw, as `driver→driven` (`wireKey`). */
  wires: ReadonlySet<string>;
  /** Gates and doors that wear their pips. */
  gates: ReadonlySet<number>;
  /** Locks that wear their progress pips. */
  locks: ReadonlySet<number>;
}

export const wireKey = (from: number, to: number): string => `${from}→${to}`;

export const NO_REVEAL: HintReveal = { wires: new Set(), gates: new Set(), locks: new Set() };

/**
 * What the hints bought so far let the board draw. `hinted`: the fixtures any bought hint pointed
 * at; `stepped`: those a Step pointed at (a subset).
 */
export function hintReveal(fixtures: readonly FixtureView[], hinted: ReadonlySet<number>, stepped: ReadonlySet<number> = new Set()): HintReveal {
  if (hinted.size === 0 && stepped.size === 0) return NO_REVEAL;
  const wires = new Set<string>(), gates = new Set<number>(), locks = new Set<number>();
  // a Step's gate: the fixture itself if it is one, else what it drives
  const spelled = new Set<number>();
  for (const f of fixtures) if (stepped.has(f.id)) for (const g of f.kind === 'Gate' || f.kind === 'Door' ? [f.id] : f.drives) spelled.add(g);
  for (const f of fixtures) {
    if ((hinted.has(f.id) || stepped.has(f.id)) && (f.kind === 'Gate' || f.kind === 'Door')) gates.add(f.id);
    if (f.kind === 'Lock' && [f.id, ...(f.inputs ?? [])].some((i) => hinted.has(i) || stepped.has(i))) locks.add(f.id);
    for (const d of f.drives) {
      if (!hinted.has(f.id) && !hinted.has(d) && !stepped.has(f.id) && !spelled.has(d)) continue;
      wires.add(wireKey(f.id, d));
      gates.add(d);
    }
  }
  return { wires, gates, locks };
}
